import { get, writable } from "svelte/store";
import { api, ApiError, type AuthSession, type SessionUser } from "@/api";
import { disconnect as disconnectConsole } from "@/stores/console";

const TOKEN_KEY = "mars_token";
const USER_KEY = "mars_user";
let sessionVersion = 0;

function loadCachedUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    const cached: unknown = raw ? JSON.parse(raw) : null;
    const invalidContainer = typeof cached !== "object" || cached === null || Array.isArray(cached);
    if (invalidContainer) {
      return null;
    }
    if (!("id" in cached) || !("username" in cached) || !("role" in cached)) {
      return null;
    }
    const { id, username, role } = cached;
    const invalidIdentity = typeof id !== "string" || typeof username !== "string";
    if (invalidIdentity) {
      return null;
    }
    const invalidRole = typeof role !== "string";
    if (invalidRole) {
      return null;
    }
    return { ...cached, id, username, role };
  } catch {
    return null;
  }
}

export const token = writable<string | null>(localStorage.getItem(TOKEN_KEY));
export const user = writable<SessionUser | null>(loadCachedUser());
export const checking = writable(true);
export const sessionRecoveryNeeded = writable(false);
export const sessionNotice = writable("");

export function authenticate(session: AuthSession): void {
  ++sessionVersion;
  sessionNotice.set("");
  sessionRecoveryNeeded.set(false);
  localStorage.setItem(TOKEN_KEY, session.accessToken);
  localStorage.setItem(USER_KEY, JSON.stringify(session.user));
  token.set(session.accessToken);
  user.set(session.user);
}

export function logout(): void {
  ++sessionVersion;
  sessionNotice.set("");
  sessionRecoveryNeeded.set(false);
  const current = localStorage.getItem(TOKEN_KEY);
  disconnectConsole();
  if (current) {
    void api.logout(current).catch(() => {});
  }
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  token.set(null);
  user.set(null);
}

export function logoutAfterPasswordChange(): void {
  logout();
  sessionNotice.set("Пароль изменён. Войдите снова с новым паролем.");
}

export async function refreshSessionProfile(): Promise<void> {
  const current = get(token);
  const version = sessionVersion;
  if (!current) {
    throw new Error("Нет активной сессии");
  }
  const profile = await api.me(current);
  if (version !== sessionVersion || get(token) !== current) {
    return;
  }
  localStorage.setItem(USER_KEY, JSON.stringify(profile));
  user.set(profile);
}

export async function initSession(): Promise<void> {
  const current = localStorage.getItem(TOKEN_KEY);
  if (!current) {
    sessionRecoveryNeeded.set(false);
    checking.set(false);
    return;
  }

  checking.set(true);
  const version = sessionVersion;
  try {
    const profile = await api.me(current);
    if (version !== sessionVersion || localStorage.getItem(TOKEN_KEY) !== current) {
      return;
    }
    localStorage.setItem(USER_KEY, JSON.stringify(profile));
    user.set(profile);
    token.set(current);
    sessionRecoveryNeeded.set(false);
  } catch (err) {
    if (version !== sessionVersion || localStorage.getItem(TOKEN_KEY) !== current) {
      return;
    }
    if (err instanceof ApiError && err.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      token.set(null);
      user.set(null);
      sessionRecoveryNeeded.set(false);
    } else {
      token.set(current);
      sessionRecoveryNeeded.set(get(user) === null);
    }
  } finally {
    checking.set(false);
  }
}

/** Explicit retry for a saved token whose profile could not be loaded. */
export function retrySession(): Promise<void> {
  if (get(checking)) {
    return Promise.resolve();
  }
  return initSession();
}

if (typeof window !== "undefined") {
  window.addEventListener("mars:unauthorized", (event) => {
    if (event instanceof CustomEvent) {
      const detail: unknown = event.detail;
      const hasToken =
        typeof detail === "object" &&
        detail !== null &&
        "token" in detail &&
        typeof detail.token === "string";
      const belongsToPreviousSession = hasToken && detail.token !== localStorage.getItem(TOKEN_KEY);
      if (belongsToPreviousSession) {
        return;
      }
    }
    logout();
  });
}
